import React, { useEffect, useState } from "react";
import clsx from "clsx";

import { makeStyles, alpha } from "@material-ui/core/styles";
import Pagination from "@material-ui/lab/Pagination";
import PaginationItem from "@material-ui/lab/PaginationItem";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    alignItems: "center",
    "& .MuiPagination-ul": {
      flexWrap: "nowrap"
    }
  },
  // Mesmas métricas do PaginationItem sizeSmall, para o input não destoar dos
  // botões vizinhos.
  pageInput: {
    height: 26,
    minWidth: 26,
    borderRadius: 13,
    margin: "0 1px",
    padding: "0 4px",
    textAlign: "center",
    fontSize: theme.typography.pxToRem(13),
    fontFamily: theme.typography.fontFamily,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    color: theme.palette.text.primary,
    outline: "none",
    "&:focus": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2)
    },
    "&:disabled": {
      borderColor: theme.palette.action.disabled,
      backgroundColor: "transparent",
      color: theme.palette.action.disabled
    }
  }
}));

// A página atual vira um campo editável: digitar o número e Enter (ou sair do
// campo) navega direto. `page` aqui é base 1, como o Pagination exibe.
const CurrentPageInput = ({ page, totalPages, disabled, onCommit }) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(String(page));

  useEffect(() => {
    setDraft(String(page));
  }, [page]);

  const commit = (event) => {
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(page));
      return;
    }
    const clamped = Math.min(Math.max(parsed, 1), totalPages);
    setDraft(String(clamped));
    if (clamped !== page) onCommit(event, clamped);
  };

  const width = `calc(${Math.max(String(totalPages).length, draft.length, 1)}ch + 12px)`;

  return (
    <input
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      className={classes.pageInput}
      style={{ width }}
      value={draft}
      disabled={disabled}
      aria-label={i18n.t("pagination.currentPage")}
      title={i18n.t("pagination.currentPageHint", { total: totalPages })}
      onChange={(event) => setDraft(event.target.value.replace(/\D/g, ""))}
      onFocus={(event) => event.target.select()}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          commit(event);
          event.currentTarget.blur();
        } else if (event.key === "Escape") {
          setDraft(String(page));
          event.currentTarget.blur();
        }
      }}
    />
  );
};

/**
 * ActionsComponent para o TablePagination do MUI v4: troca as setas simples por
 * paginação numerada (atual ±2, primeira/última sempre visíveis, pular para os
 * extremos) com a página atual editável. Recebe o contrato que o
 * TablePagination repassa (page base 0) e converte para o Pagination do lab
 * (base 1).
 */
const TablePaginationActions = ({
  className,
  count,
  page,
  rowsPerPage,
  onPageChange,
  onChangePage,
  backIconButtonProps: _backIconButtonProps,
  nextIconButtonProps: _nextIconButtonProps,
  ...rest
}) => {
  const classes = useStyles();
  const handlePageChange = onPageChange || onChangePage;
  const totalPages = Math.max(1, Math.ceil(count / rowsPerPage));

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      <Pagination
        count={totalPages}
        page={page + 1}
        siblingCount={2}
        boundaryCount={1}
        showFirstButton
        showLastButton
        size="small"
        disabled={count === 0}
        onChange={(event, newPage) => handlePageChange(event, newPage - 1)}
        renderItem={(item) =>
          item.type === "page" && item.selected ? (
            <CurrentPageInput
              page={page + 1}
              totalPages={totalPages}
              disabled={count === 0}
              onCommit={(event, newPage) => handlePageChange(event, newPage - 1)}
            />
          ) : (
            <PaginationItem {...item} />
          )
        }
      />
    </div>
  );
};

export default TablePaginationActions;
